import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import {
  HeartHandshake,
  UserPlus,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Calendar,
  Clock,
  Filter,
  Search,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const BUCKETS = [
  { key: "unassigned", label: "Received / Unassigned" },
  { key: "awaiting_acceptance", label: "Awaiting Acceptance" },
  { key: "active_care", label: "Active Care" },
  { key: "follow_up_due", label: "Follow-Up Due" },
  { key: "overdue", label: "Overdue" },
  { key: "reassignment_needed", label: "Reassignment Needed" },
  { key: "urgent_admin_review", label: "Urgent Admin Review" },
  { key: "completed", label: "Completed / Closed" },
];

const PRIORITY_LABELS = {
  routine: "Routine",
  time_sensitive: "Time-Sensitive",
  urgent_admin_review: "Urgent Admin Review",
};

const STAGE_LABELS = {
  unassigned: "Unassigned",
  awaiting_acceptance: "Awaiting Acceptance",
  active_care: "Active Care",
  completed: "Completed",
};

function StageBadge({ stage }) {
  const styles = {
    unassigned: "bg-slate-100 text-slate-700 border-slate-200",
    awaiting_acceptance: "bg-amber-50 text-amber-800 border-amber-200",
    active_care: "bg-emerald-50 text-emerald-800 border-emerald-200",
    completed: "bg-slate-50 text-slate-500 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles[stage] || styles.unassigned}`}>
      {STAGE_LABELS[stage] || stage}
    </span>
  );
}

function PriorityBadge({ priority }) {
  if (!priority || priority === "routine") return null;
  const styles = {
    time_sensitive: "bg-orange-50 text-orange-800 border-orange-200",
    urgent_admin_review: "bg-red-50 text-red-800 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles[priority] || ""}`}>
      {PRIORITY_LABELS[priority] || priority}
    </span>
  );
}

function BucketCard({ bucket, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`care-bucket-${bucket.key}`}
      className={`text-left p-4 rounded-2xl border transition-colors ${
        active ? "border-[#4A6FA5] ring-2 ring-[#4A6FA5]/20 bg-white" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{bucket.label}</p>
      <p className="font-heading text-3xl text-slate-900 mt-1 tabular-nums">{count ?? "—"}</p>
    </button>
  );
}

export default function AdminCareCoordination() {
  const [dashboard, setDashboard] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("unassigned");
  const [chaplains, setChaplains] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [filters, setFilters] = useState({ organization_id: "", assigned_to: "", priority: "", source: "", q: "" });
  const [selectedId, setSelectedId] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Deep-link support: /care-coordination?open=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get("open");
    if (openId) setSelectedId(openId);
  }, [location.search]);

  const loadDashboard = async () => {
    try {
      const d = await api.get("/admin/care-coordination/dashboard");
      setDashboard(d.data);
    } catch (e) {
      // silent — retry on next mount
    }
  };

  const loadRecords = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("bucket", bucket);
      for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
      const r = await api.get(`/admin/care-coordination?${params.toString()}`);
      setRecords(r.data || []);
    } catch (e) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      const [chapRes, orgRes] = await Promise.all([
        api.get("/admin/care-coordination/assignable/chaplains").catch(() => ({ data: [] })),
        api.get("/admin/organizations").catch(() => ({ data: [] })),
      ]);
      setChaplains(chapRes.data || []);
      setOrganizations(orgRes.data || []);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => { loadOptions(); }, []);
  useEffect(() => { loadDashboard(); loadRecords(); /* eslint-disable-next-line */ }, [bucket, filters]);

  return (
    <div data-testid="admin-care-coordination">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <HeartHandshake className="w-6 h-6 text-[#4A6FA5]" strokeWidth={1.75} /> Care Coordination
          </h2>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Operational continuity for every employee-support request — clear owner, current status, next action, and follow-up.
            Reminder: CCCHAPS is not an emergency service. Emergencies should be directed to 911.
          </p>
        </div>
      </div>

      {/* Buckets */}
      <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {BUCKETS.map((b) => (
          <BucketCard
            key={b.key}
            bucket={b}
            count={dashboard?.buckets?.[b.key]}
            active={bucket === b.key}
            onClick={() => setBucket(b.key)}
          />
        ))}
      </div>

      {/* Filters */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <select
            data-testid="care-filter-org"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            value={filters.organization_id}
            onChange={(e) => setFilters((f) => ({ ...f, organization_id: e.target.value }))}
          >
            <option value="">All organizations</option>
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <select
            data-testid="care-filter-chaplain"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            value={filters.assigned_to}
            onChange={(e) => setFilters((f) => ({ ...f, assigned_to: e.target.value }))}
          >
            <option value="">All chaplains</option>
            {chaplains.map((c) => (
              <option key={c.id} value={c.id}>{c.name || c.email} ({c.role})</option>
            ))}
          </select>
          <select
            data-testid="care-filter-priority"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            value={filters.priority}
            onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}
          >
            <option value="">Any priority</option>
            <option value="routine">Routine</option>
            <option value="time_sensitive">Time-Sensitive</option>
            <option value="urgent_admin_review">Urgent Admin Review</option>
          </select>
          <select
            data-testid="care-filter-source"
            className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
            value={filters.source}
            onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}
          >
            <option value="">Any source</option>
            <option value="care_portal_support_request">Care Portal support request</option>
            <option value="callachap">CALLACHAP</option>
            <option value="phone_call">Phone call</option>
            <option value="organization_referral">Organization / HR referral</option>
            <option value="chaplain_created">Chaplain-created</option>
            <option value="administrator_created">Administrator-created</option>
            <option value="other">Other</option>
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="care-filter-search"
              type="text"
              placeholder="Search name or organization"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* Records list */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="care-records-list">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <p className="text-sm font-medium text-slate-700">
            {BUCKETS.find((b) => b.key === bucket)?.label} · <span className="text-slate-500">{records.length} record{records.length === 1 ? "" : "s"}</span>
          </p>
        </div>
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Loading…</p>
        ) : records.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">No records in this bucket.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-semibold">Employee</th>
                <th className="text-left px-4 py-2 font-semibold">Organization</th>
                <th className="text-left px-4 py-2 font-semibold">Stage</th>
                <th className="text-left px-4 py-2 font-semibold">Priority</th>
                <th className="text-left px-4 py-2 font-semibold">Assigned</th>
                <th className="text-left px-4 py-2 font-semibold">Follow-Up</th>
                <th className="text-left px-4 py-2 font-semibold">Received</th>
                <th className="text-right px-4 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-800">{r.first_name} {r.last_name}</td>
                  <td className="px-4 py-2 text-slate-600">{r.company_name}</td>
                  <td className="px-4 py-2"><StageBadge stage={r.care_stage} /></td>
                  <td className="px-4 py-2"><PriorityBadge priority={r.priority} /></td>
                  <td className="px-4 py-2 text-slate-600">{r.assigned_to_name || <span className="text-slate-400 italic">unassigned</span>}</td>
                  <td className="px-4 py-2 text-slate-600">{r.follow_up_date || "—"}</td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setSelectedId(r.id)}
                      data-testid={`care-open-${r.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[#4A6FA5] hover:bg-blue-50"
                    >
                      Open <ArrowRight className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId && (
        <CareRecordDrawer
          recordId={selectedId}
          chaplains={chaplains}
          onClose={() => {
            setSelectedId(null);
            const params = new URLSearchParams(location.search);
            if (params.has("open")) {
              params.delete("open");
              navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : "" }, { replace: true });
            }
          }}
          onChanged={() => { loadDashboard(); loadRecords(); }}
        />
      )}
    </div>
  );
}

function CareRecordDrawer({ recordId, chaplains, onClose, onChanged }) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [priority, setPriority] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [followUpDate, setFollowUpDate] = useState("");
  const [adminNote, setAdminNote] = useState("");
  const [declineReason, setDeclineReason] = useState("unavailable");
  const [declineNote, setDeclineNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/care-coordination/${recordId}`);
      setRec(r.data);
      setPriority(r.data.priority || "routine");
      setNextAction(r.data.next_action || "");
      setFollowUpDate(r.data.follow_up_date || "");
      setAdminNote(r.data.admin_note || "");
    } catch (e) {
      toast.error("Failed to load record");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recordId]);

  const doAction = async (fn, label) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      await load();
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `${label} failed`);
    } finally {
      setBusy(false);
    }
  };

  const stage = rec?.care_stage;

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" data-testid="care-record-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="ml-auto relative w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <p className="font-heading text-lg text-slate-900">Care Record</p>
          <button type="button" onClick={onClose} data-testid="care-drawer-close" className="text-slate-500 hover:text-slate-900">×</button>
        </div>

        {loading || !rec ? (
          <p className="p-6 text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="p-6 space-y-6">
            {/* Header */}
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <StageBadge stage={rec.care_stage} />
                <PriorityBadge priority={rec.priority} />
                {rec.needs_reassignment && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-amber-50 text-amber-800 border-amber-200">
                    Reassignment needed
                  </span>
                )}
              </div>
              <h3 className="mt-3 font-heading text-xl text-slate-900">
                {rec.first_name} {rec.last_name}
              </h3>
              <p className="text-sm text-slate-600 mt-1">{rec.company_name}</p>
              <p className="mt-3 text-xs text-slate-500">
                Received {new Date(rec.created_at).toLocaleString()} · Source: {rec.source || "care_portal_support_request"} · Urgency: {rec.urgency}
              </p>
            </div>

            {/* Assignment block */}
            <section className="border border-slate-200 rounded-xl p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Assignment</p>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="text-sm text-slate-700">
                  {rec.assigned_to_name || <span className="italic text-slate-500">Not assigned</span>}
                </p>
                {rec.assigned_at && <p className="text-xs text-slate-500">since {new Date(rec.assigned_at).toLocaleString()}</p>}
              </div>
              {stage !== "completed" && (
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  <select
                    data-testid="care-assign-chaplain"
                    className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    value={assignTo}
                    onChange={(e) => setAssignTo(e.target.value)}
                  >
                    <option value="">Choose assignee…</option>
                    {chaplains.map((c) => (
                      <option key={c.id} value={c.id}>{c.name || c.email} ({c.role})</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    data-testid="care-btn-assign"
                    disabled={!assignTo || busy}
                    onClick={() => doAction(() => api.patch(`/admin/care-coordination/${rec.id}/assign`, { assigned_to: assignTo, priority: priority || "routine", next_action: nextAction || undefined }), rec.assigned_to ? "Reassigned" : "Assigned")}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#4A6FA5] text-white hover:bg-[#3B597B] disabled:opacity-50"
                  >
                    <UserPlus className="w-4 h-4" /> {rec.assigned_to ? "Reassign" : "Assign"}
                  </button>
                </div>
              )}
            </section>

            {/* Accept / Decline / Complete / Escalate */}
            {stage !== "completed" && (
              <section className="border border-slate-200 rounded-xl p-4 space-y-3">
                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Workflow</p>
                <div className="flex flex-wrap gap-2">
                  {stage === "awaiting_acceptance" && (
                    <button type="button" data-testid="care-btn-accept" disabled={busy}
                      onClick={() => doAction(() => api.post(`/admin/care-coordination/${rec.id}/accept`), "Accepted")}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                      <CheckCircle2 className="w-4 h-4" /> Accept
                    </button>
                  )}
                  <button type="button" data-testid="care-btn-escalate" disabled={busy}
                    onClick={() => doAction(() => api.post(`/admin/care-coordination/${rec.id}/escalate`, { escalate: rec.priority !== "urgent_admin_review" }), rec.priority !== "urgent_admin_review" ? "Escalated" : "De-escalated")}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50">
                    <AlertTriangle className="w-4 h-4" /> {rec.priority === "urgent_admin_review" ? "Remove escalation" : "Escalate for admin review"}
                  </button>
                  {(stage === "awaiting_acceptance" || stage === "active_care") && (
                    <button type="button" data-testid="care-btn-complete" disabled={busy}
                      onClick={() => doAction(() => api.post(`/admin/care-coordination/${rec.id}/complete`, { confirm: true, follow_up_resolution: rec.follow_up_date ? "follow_up_completed" : "no_follow_up_needed" }), "Completed")}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">
                      <CheckCircle2 className="w-4 h-4" /> Complete / Close
                    </button>
                  )}
                </div>
                {stage === "awaiting_acceptance" && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-xs text-slate-500 font-medium">Decline this assignment</p>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                      <select
                        data-testid="care-decline-reason"
                        className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                      >
                        <option value="unavailable">Unavailable</option>
                        <option value="scheduling_conflict">Scheduling conflict</option>
                        <option value="outside_service_area">Outside service area</option>
                        <option value="conflict_of_interest">Conflict of interest</option>
                        <option value="other">Other</option>
                      </select>
                      <button
                        type="button"
                        data-testid="care-btn-decline"
                        disabled={busy}
                        onClick={() => doAction(async () => {
                          await api.post(`/admin/care-coordination/${rec.id}/decline`, { reason: declineReason, note: declineNote || undefined });
                          onClose();
                        }, "Declined")}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        <XCircle className="w-4 h-4" /> Decline
                      </button>
                    </div>
                    <input
                      type="text"
                      value={declineNote}
                      onChange={(e) => setDeclineNote(e.target.value)}
                      placeholder="Optional operational note"
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    />
                  </div>
                )}
              </section>
            )}

            {/* Operational fields */}
            <section className="border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Operational fields</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm">
                  <span className="text-xs text-slate-500 font-medium">Priority</span>
                  <select
                    data-testid="care-field-priority"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                  >
                    <option value="routine">Routine</option>
                    <option value="time_sensitive">Time-Sensitive</option>
                    <option value="urgent_admin_review">Urgent Admin Review</option>
                  </select>
                </label>
                <label className="text-sm">
                  <span className="text-xs text-slate-500 font-medium">Follow-up date</span>
                  <input
                    type="date"
                    data-testid="care-field-followup"
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                    value={followUpDate || ""}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                  />
                </label>
              </div>
              <label className="text-sm block">
                <span className="text-xs text-slate-500 font-medium">Next action</span>
                <input
                  type="text"
                  data-testid="care-field-next-action"
                  maxLength={200}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  value={nextAction}
                  onChange={(e) => setNextAction(e.target.value)}
                  placeholder="e.g., Return phone call"
                />
              </label>
              <label className="text-sm block">
                <span className="text-xs text-slate-500 font-medium">Internal admin note (owner/admin only)</span>
                <textarea
                  data-testid="care-field-admin-note"
                  rows={3}
                  maxLength={2000}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                />
              </label>
              <button
                type="button"
                data-testid="care-btn-save-fields"
                disabled={busy}
                onClick={() => doAction(() => api.patch(`/admin/care-coordination/${rec.id}/fields`, {
                  priority,
                  next_action: nextAction,
                  follow_up_date: followUpDate,
                  admin_note: adminNote,
                }), "Saved")}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-[#4A6FA5] text-white hover:bg-[#3B597B] disabled:opacity-50"
              >
                Save fields
              </button>
            </section>

            {/* Care activities (existing) */}
            <section className="border border-slate-200 rounded-xl p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Recorded contacts</p>
              {!rec.activities?.length ? (
                <p className="mt-2 text-sm text-slate-500">No contacts recorded yet. Use the Support Requests page to record a contact — activities are the single source that also feeds Activity Reports.</p>
              ) : (
                <ul className="mt-2 divide-y divide-slate-100">
                  {rec.activities.map((a) => (
                    <li key={a.id} className="py-2 text-sm">
                      <p className="text-slate-800">
                        <Calendar className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
                        {a.date} {a.time} · <span className="text-slate-600">{a.contact_type}</span>
                      </p>
                      {a.follow_up_needed && a.follow_up_date && (
                        <p className="text-xs text-slate-500 mt-0.5">Follow-up: {a.follow_up_date}</p>
                      )}
                      {a.areas_of_support?.length ? (
                        <p className="text-xs text-slate-500 mt-0.5">Areas: {a.areas_of_support.join(", ")}</p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* History */}
            <section className="border border-slate-200 rounded-xl p-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Activity history</p>
              {!rec.status_history?.length ? (
                <p className="mt-2 text-sm text-slate-500">No history entries yet.</p>
              ) : (
                <ol className="mt-2 space-y-2">
                  {rec.status_history.slice().reverse().map((h, i) => (
                    <li key={i} className="text-sm">
                      <p className="text-slate-800">
                        <Clock className="inline w-3.5 h-3.5 mr-1 text-slate-400" />
                        <span className="font-medium">{h.type}</span>
                        {h.from_stage && h.to_stage ? ` · ${h.from_stage} → ${h.to_stage}` : ""}
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(h.at).toLocaleString()} · {h.by_name}
                        {h.reason ? ` · reason: ${h.reason}` : ""}
                        {h.assigned_to_name ? ` · to: ${h.assigned_to_name}` : ""}
                        {h.priority ? ` · priority: ${h.priority}` : ""}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
